import { computed, Injectable, signal } from '@angular/core';
import { SocketService } from '@core/services/socket.service';
import {
  IGiveaway,
  IGiveawayReward,
  IGiveawayRoll,
} from './interfaces/giveaways.interface';
import { Skin } from '@core/interfaces/skin.interface';
import { v4 as uuidv4 } from 'uuid';
import { sleep, sleepWithId } from '@core/utils/sleep.util';

@Injectable({
  providedIn: 'root',
})
export class GiveawaysService {
  timeoutIds = signal<any[]>([]);
  giveawaysRoot = signal<IGiveaway>({
    ends: 0,
    players: [],
    rewards: [],
  });
  giveawaysRoll = signal<IGiveawayRoll | null>(null);
  readonly animationSettings = {
    TIMER_DURATION: 11000,
    ROLL_MAIN_DURATION: 5000,
    ROLL_CLOSER_DURATION: 300,
    ROLL_RESULTS_DURATION: 3000,
    FINAL_RESULTS_DURATION: 6000,
    WIN_INDEX: 87,
    MIN_ITEMS: 100,
    RESET_SCREEN_DURATION: 3000,
  };
  animation = signal<
    'rolling' | 'roll-results' | 'final-results' | 'timer' | null
  >(null);
  isReset = signal<boolean>(false);

  readonly rewards = computed<Skin[]>(() => {
    const rootData = this.giveawaysRoot();
    if (rootData && rootData.rewards) {
      return this.transformRewardsWithAssetId(rootData.rewards);
    }
    return [];
  });

  constructor(private socketService: SocketService) {
    this.socketService.socket.on(
      'update_giveaway_page',
      (data: IGiveaway[]) => {
        this.giveawaysRoot.set(data[0]);
      }
    );
    // this.socketService.emit('get_giveaway_page');

    this.socketService.socket.on(
      'giveaway_roll',
      async (data: IGiveawayRoll) => {
        this.giveawayRoll(data);
      }
    );
  }

  // async test() {
  //   const item = this.giveawaysRoot()?.rewards.at(0)!;
  //   const winner = players.find((p) => p.name === 'zdraste')!;
  //   const data: IGiveawayRoll = {
  //     serialid: '',
  //     seed: '',
  //     roll: '',
  //     avatars: players,
  //     item: {
  //       ...item,
  //       winner,
  //     },
  //     winner,
  //   };
  //   await this.giveawayRoll(data);
  // }

  private async giveawayRoll(data: IGiveawayRoll) {
    const isRunning = this.timeoutIds().length > 0 || this.animation() !== null;

    if (isRunning) {
      this.toggleReset();
      this.resetTimeouts();
      this.resetAnimation();
      await sleep(100);
      this.toggleReset();

      await sleep(this.animationSettings.RESET_SCREEN_DURATION);
    }

    const {
      FINAL_RESULTS_DURATION,
      ROLL_CLOSER_DURATION,
      ROLL_MAIN_DURATION,
      TIMER_DURATION,
      ROLL_RESULTS_DURATION,
    } = this.animationSettings;

    this.giveawaysRoll.set(data);
    this.animation.set('timer');
    const { promise: p1, timeoutId: t1 } = await sleepWithId(TIMER_DURATION);
    if (t1) {
      this.timeoutIds.update((ids) => [...ids, t1]);
    }
    await p1;

    this.animation.set('rolling');
    const { promise: p2, timeoutId: t2 } = await sleepWithId(
      ROLL_MAIN_DURATION + ROLL_CLOSER_DURATION
    );
    if (t2) {
      this.timeoutIds.update((ids) => [...ids, t2]);
    }
    await p2;

    this.animation.set('roll-results');
    const { promise: p3, timeoutId: t3 } = await sleepWithId(
      ROLL_RESULTS_DURATION
    );
    if (t3) {
      this.timeoutIds.update((ids) => [...ids, t3]);
    }
    await p3;

    this.animation.set('final-results');
    const { promise: p4, timeoutId: t4 } = await sleepWithId(
      FINAL_RESULTS_DURATION
    );
    if (t4) {
      this.timeoutIds.update((ids) => [...ids, t4]);
    }
    await p4;

    this.resetTimeouts();
    this.resetAnimation();
  }

  private clearTimeouts() {
    this.timeoutIds().forEach((t) => {
      clearTimeout(t);
    });
  }

  private resetAnimation() {
    this.animation.set(null);
  }

  private resetTimeouts() {
    this.clearTimeouts();
    this.timeoutIds.set([]);
  }

  transformRewardsWithAssetId(rewards: IGiveawayReward[]): Skin[] {
    return rewards.map((reward) => ({
      ...reward,
      assetid: String(uuidv4()),
    }));
  }

  public toggleReset() {
    this.isReset.update((b) => !b);
  }
}
